import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Link,
  Button,
} from '@mui/material';
import {
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';

interface SerpQuery {
  query: string;
  timestamp: string;
  raw_html_file: string;
  domain_rankings: Record<string, any>;
  organic_results_count: number;
  local_results_count: number;
  total_results: number;
  full_serp_data?: any; // Complete SERP response for exploration
}

interface SerpVisualizationProps {
  queries: SerpQuery[];
  selectedQuery?: string;
  onQueryChange?: (query: string) => void;
}

interface JsonViewerProps {
  data: any;
  onElementHover?: (path: string) => void;
  onElementClick?: (path: string) => void;
  highlightedPath?: string;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ 
  data, 
  onElementHover, 
  onElementClick, 
  highlightedPath 
}) => {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set(['root']));

  const toggleExpanded = (path: string) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedPaths(newExpanded);
  };

  const renderValue = (value: any, path: string, key?: string): JSX.Element => {
    const isHighlighted = highlightedPath === path;
    const isExpanded = expandedPaths.has(path);

    if (value === null) {
      return (
        <span 
          style={{ 
            color: '#999',
            backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
            padding: '2px 4px',
            borderRadius: '3px'
          }}
          onMouseEnter={() => onElementHover?.(path)}
          onClick={() => onElementClick?.(path)}
        >
          null
        </span>
      );
    }

    if (typeof value === 'string') {
      return (
        <span 
          style={{ 
            color: '#d14',
            backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
            padding: '2px 4px',
            borderRadius: '3px'
          }}
          onMouseEnter={() => onElementHover?.(path)}
          onClick={() => onElementClick?.(path)}
        >
          "{value}"
        </span>
      );
    }

    if (typeof value === 'number') {
      return (
        <span 
          style={{ 
            color: '#0969da',
            backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
            padding: '2px 4px',
            borderRadius: '3px'
          }}
          onMouseEnter={() => onElementHover?.(path)}
          onClick={() => onElementClick?.(path)}
        >
          {value}
        </span>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <span 
          style={{ 
            color: '#0969da',
            backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
            padding: '2px 4px',
            borderRadius: '3px'
          }}
          onMouseEnter={() => onElementHover?.(path)}
          onClick={() => onElementClick?.(path)}
        >
          {value.toString()}
        </span>
      );
    }

    if (Array.isArray(value)) {
      return (
        <Box sx={{ ml: 2 }}>
          <Box 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
              padding: '2px 4px',
              borderRadius: '3px'
            }}
            onClick={() => toggleExpanded(path)}
            onMouseEnter={() => onElementHover?.(path)}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666', 
                fontFamily: 'monospace',
                fontWeight: 'bold'
              }}
            >
              [{value.length}]
            </Typography>
            <ExpandMoreIcon 
              fontSize="small" 
              sx={{ 
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </Box>
          {isExpanded && (
            <Box sx={{ ml: 2, borderLeft: '1px solid #ddd', pl: 2 }}>
              {value.map((item, index) => (
                <Box key={index} sx={{ my: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    component="span" 
                    sx={{ color: '#666', fontFamily: 'monospace' }}
                  >
                    {index}:
                  </Typography>
                  <Box sx={{ ml: 1, display: 'inline' }}>
                    {renderValue(item, `${path}[${index}]`)}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      return (
        <Box sx={{ ml: 2 }}>
          <Box 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
              padding: '2px 4px',
              borderRadius: '3px'
            }}
            onClick={() => toggleExpanded(path)}
            onMouseEnter={() => onElementHover?.(path)}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666', 
                fontFamily: 'monospace',
                fontWeight: 'bold'
              }}
            >
              {`{${keys.length}}`}
            </Typography>
            <ExpandMoreIcon 
              fontSize="small" 
              sx={{ 
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </Box>
          {isExpanded && (
            <Box sx={{ ml: 2, borderLeft: '1px solid #ddd', pl: 2 }}>
              {keys.map((objKey) => (
                <Box key={objKey} sx={{ my: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    component="span" 
                    sx={{ color: '#0969da', fontFamily: 'monospace', fontWeight: 'bold' }}
                  >
                    "{objKey}":
                  </Typography>
                  <Box sx={{ ml: 1, display: 'inline' }}>
                    {renderValue(value[objKey], `${path}.${objKey}`, objKey)}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    return <span>{String(value)}</span>;
  };

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        height: '60vh', 
        overflow: 'auto',
        backgroundColor: '#f8f9fa',
        fontFamily: 'monospace'
      }}
    >
      {renderValue(data, 'root')}
    </Paper>
  );
};

const SerpVisualization: React.FC<SerpVisualizationProps> = ({
  queries,
  selectedQuery,
  onQueryChange
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [currentQuery, setCurrentQuery] = useState<SerpQuery | null>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedPath, setHighlightedPath] = useState<string>('');
  const [showSideBySide, setShowSideBySide] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Set the first query as current if not specified
  useEffect(() => {
    if (queries.length > 0) {
      const queryToLoad = selectedQuery 
        ? queries.find(q => q.query === selectedQuery) || queries[0]
        : queries[0];
      setCurrentQuery(queryToLoad);
    }
  }, [queries, selectedQuery]);

  // Load HTML content when current query changes
  useEffect(() => {
    if (currentQuery?.raw_html_file) {
      loadHtmlContent(currentQuery.raw_html_file);
    }
  }, [currentQuery]);

  const loadHtmlContent = async (htmlUrl: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // First try to fetch via our proxy endpoint to handle CORS
      const proxyUrl = `/api/v1/serp/proxy-html?url=${encodeURIComponent(htmlUrl)}`;
      
      try {
        const response = await fetch(proxyUrl, {
          headers: {
            ...(typeof window !== 'undefined' && localStorage.getItem('authguard_access_token')
              ? { Authorization: `Bearer ${localStorage.getItem('authguard_access_token')}` }
              : {}),
          }
        });
        if (response.ok) {
          const html = await response.text();
          setHtmlContent(html);
          return;
        }
      } catch (proxyError) {
        console.warn('Proxy fetch failed, trying direct fetch:', proxyError);
      }
      
      // Fallback to direct fetch (will likely fail due to CORS)
      const response = await fetch(htmlUrl);
      if (!response.ok) {
        throw new Error(`Failed to load HTML: ${response.statusText}`);
      }
      const html = await response.text();
      setHtmlContent(html);
    } catch (err: any) {
      // Handle CORS and other fetch errors gracefully
      const isCorsError = err.message.includes('CORS') || 
                         err.message.includes('cross-origin') ||
                         err.name === 'TypeError';
      
      if (isCorsError) {
        setError('Cannot load HTML due to CORS restrictions. Click "Open raw HTML in new tab" to view the results.');
      } else {
        setError(err.message || 'Failed to load HTML content');
      }
      setHtmlContent('');
    } finally {
      setLoading(false);
    }
  };

  const handleQueryChange = (query: SerpQuery) => {
    setCurrentQuery(query);
    onQueryChange?.(query.query);
  };

  const handleElementHover = (path: string) => {
    setHighlightedPath(path);
    // TODO: Implement HTML highlighting based on JSON path
  };

  const handleElementClick = (path: string) => {
    // TODO: Scroll to corresponding element in HTML
    console.log('Clicked path:', path);
  };

  const renderQueryInfo = (query: SerpQuery) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon color="primary" />
              {query.query}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(query.timestamp).toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {query.raw_html_file && (
              <Tooltip title="Open raw HTML in new tab">
                <IconButton 
                  size="small"
                  onClick={() => window.open(query.raw_html_file, '_blank')}
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Organic Results
            </Typography>
            <Chip label={query.organic_results_count} size="small" />
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Local Results
            </Typography>
            <Chip label={query.local_results_count} size="small" color="info" />
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Total Results
            </Typography>
            <Chip label={query.total_results.toLocaleString()} size="small" color="secondary" />
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Domain Rankings
            </Typography>
            <Chip label={Object.keys(query.domain_rankings).length} size="small" color="success" />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (queries.length === 0) {
    return (
      <Alert severity="info">
        No SERP data available for visualization. Raw HTML files are required from SerpAPI responses.
      </Alert>
    );
  }

  if (!currentQuery) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Query Selection */}
      {queries.length > 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Query to Visualize
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {queries.map((query, index) => (
                <Chip
                  key={index}
                  label={query.query}
                  variant={currentQuery.query === query.query ? "filled" : "outlined"}
                  color={currentQuery.query === query.query ? "primary" : "default"}
                  onClick={() => handleQueryChange(query)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Current Query Info */}
      {renderQueryInfo(currentQuery)}

      {/* View Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showSideBySide}
                  onChange={(e) => setShowSideBySide(e.target.checked)}
                />
              }
              label="Side-by-side view"
            />
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="JSON + HTML" icon={<CodeIcon />} />
                <Tab label="HTML Only" icon={<VisibilityIcon />} />
              </Tabs>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* JSON Panel */}
          <Grid item xs={showSideBySide ? 6 : 12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon color="primary" />
                    Complete SERP Response Data
                  </Typography>
                  <Chip 
                    label={currentQuery?.full_serp_data ? "Complete Data" : "Summary Only"} 
                    size="small" 
                    color={currentQuery?.full_serp_data ? "success" : "warning"}
                    variant="outlined"
                  />
                </Box>
                {currentQuery && (
                  <JsonViewer
                    data={currentQuery.full_serp_data || {
                      query: currentQuery.query,
                      timestamp: currentQuery.timestamp,
                      organic_results_count: currentQuery.organic_results_count,
                      local_results_count: currentQuery.local_results_count,
                      total_results: currentQuery.total_results,
                      domain_rankings: currentQuery.domain_rankings,
                      note: "Full SERP data not available - showing summary only"
                    }}
                    onElementHover={handleElementHover}
                    onElementClick={handleElementClick}
                    highlightedPath={highlightedPath}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* HTML Panel */}
          {showSideBySide && (
            <Grid item xs={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon color="primary" />
                    Raw HTML Results
                  </Typography>
                  {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                      <CircularProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading SERP HTML content...
                      </Typography>
                    </Box>
                  ) : error ? (
                    <Alert 
                      severity="warning" 
                      action={
                        currentQuery?.raw_html_file && (
                          <Button 
                            color="inherit" 
                            size="small"
                            onClick={() => window.open(currentQuery.raw_html_file, '_blank')}
                          >
                            Open in New Tab
                          </Button>
                        )
                      }
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {error}
                      </Typography>
                      {currentQuery?.raw_html_file && (
                        <Typography variant="caption" color="text.secondary">
                          You can still view the original HTML by opening it in a new tab.
                        </Typography>
                      )}
                    </Alert>
                  ) : htmlContent ? (
                    <Box sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                      <iframe
                        ref={iframeRef}
                        srcDoc={htmlContent}
                        style={{
                          width: '100%',
                          height: '60vh',
                          border: 'none',
                          borderRadius: '4px'
                        }}
                        title="SERP Results"
                      />
                    </Box>
                  ) : (
                    <Alert severity="info">
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        No HTML content available for this query.
                      </Typography>
                      {currentQuery?.raw_html_file ? (
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => window.open(currentQuery.raw_html_file, '_blank')}
                        >
                          Open HTML in New Tab
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          This query doesn't have a raw HTML file from SerpAPI.
                        </Typography>
                      )}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* HTML Only Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityIcon color="primary" />
              Raw HTML Results
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading SERP HTML content...
                </Typography>
              </Box>
            ) : error ? (
              <Alert 
                severity="warning" 
                action={
                  currentQuery?.raw_html_file && (
                    <Button 
                      color="inherit" 
                      size="small"
                      onClick={() => window.open(currentQuery.raw_html_file, '_blank')}
                    >
                      Open in New Tab
                    </Button>
                  )
                }
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {error}
                </Typography>
                {currentQuery?.raw_html_file && (
                  <Typography variant="caption" color="text.secondary">
                    You can still view the original HTML by opening it in a new tab.
                  </Typography>
                )}
              </Alert>
            ) : htmlContent ? (
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                <iframe
                  ref={iframeRef}
                  srcDoc={htmlContent}
                  style={{
                    width: '100%',
                    height: '80vh',
                    border: 'none',
                    borderRadius: '4px'
                  }}
                  title="SERP Results"
                />
              </Box>
            ) : (
              <Alert severity="info">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No HTML content available for this query.
                </Typography>
                {currentQuery?.raw_html_file ? (
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => window.open(currentQuery.raw_html_file, '_blank')}
                  >
                    Open HTML in New Tab
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    This query doesn't have a raw HTML file from SerpAPI.
                  </Typography>
                )}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SerpVisualization; 